import { Hourglass } from "lucide-react";
import { useState } from "react";

import { PageHeader } from "@/components/shared/page-header";
import { SearchableSelect } from "@/components/shared/searchable-select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useMe } from "@/hooks/use-auth";
import { useIsMobile } from "@/hooks/use-mobile";
import { useRiderSalePendingPaymentSummary } from "@/hooks/use-rider-sales";
import { useRiders } from "@/hooks/use-riders";
import { useWorkspaces } from "@/hooks/use-workspaces";
import { formatCurrency } from "@/lib/utils";

import { RiderPendingPaymentDetailDialog } from "./rider-pending-payment-detail-dialog";

import type { RiderPendingPaymentSummary } from "@artisancode/api-types";

function WaitingBadge({ days }: { days: number }) {
  return (
    <Badge variant={days >= 7 ? "destructive" : "outline"}>{days} hari</Badge>
  );
}

function PendingPaymentCard({
  r,
  workspaceLabel,
  onShowDetail,
}: {
  r: RiderPendingPaymentSummary;
  workspaceLabel?: string;
  onShowDetail: (r: RiderPendingPaymentSummary) => void;
}) {
  return (
    <Card className="overflow-hidden border border-border shadow-xs">
      <div className="space-y-2.5 p-3.5">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="font-semibold text-sm text-foreground">
              {r.riderName}
            </p>
            {workspaceLabel && (
              <p className="text-[11px] text-muted-foreground">
                {workspaceLabel}
              </p>
            )}
          </div>
          <Badge variant={r.daysWaiting >= 7 ? "destructive" : "outline"}>
            <Hourglass className="mr-1 h-3 w-3" />
            {r.daysWaiting} hari
          </Badge>
        </div>
        <div className="grid grid-cols-2 gap-2 rounded-lg bg-muted/40 p-2.5 text-xs">
          <div>
            <span className="block text-[11px] text-muted-foreground">
              Menunggu Sejak
            </span>
            <span className="font-medium">{r.oldestDate}</span>
          </div>
          <div>
            <span className="block text-[11px] text-muted-foreground">
              Transaksi
            </span>
            <span className="font-medium">{r.pendingCount}</span>
          </div>
          <div>
            <span className="block text-[11px] text-muted-foreground">
              Total Omzet
            </span>
            <span className="font-medium">
              {formatCurrency(r.totalRevenue)}
            </span>
          </div>
          <div>
            <span className="block text-[11px] text-muted-foreground">
              Total Bonus
            </span>
            <span className="font-medium">{formatCurrency(r.totalBonus)}</span>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-full text-xs"
          onClick={() => onShowDetail(r)}
        >
          Lihat Transaksi
        </Button>
      </div>
    </Card>
  );
}

export function RiderPendingPaymentList() {
  const { data: me } = useMe();
  const { data: workspacesData } = useWorkspaces({ per_page: 100 });
  const workspaces = (workspacesData?.items ?? []).filter((w) => w.isActive);
  const { data: ridersData } = useRiders({ per_page: 100 });
  const riders = ridersData?.items ?? [];
  const [workspaceId, setWorkspaceId] = useState("");

  const { data, isLoading } = useRiderSalePendingPaymentSummary(
    workspaceId || undefined,
  );
  const items = data ?? [];
  const isMobile = useIsMobile();
  const [detail, setDetail] = useState<RiderPendingPaymentSummary | null>(null);

  const showWorkspaceLabel = Boolean(me?.canSwitchWorkspace) && !workspaceId;

  function workspaceLabelForRider(riderId: string) {
    const rider = riders.find((r) => r.id === riderId);
    if (!rider) return undefined;
    return workspaces.find((w) => w.id === rider.workspaceId)?.name;
  }

  return (
    <div>
      <PageHeader
        title="Rider Belum Dibayar"
        description="Rider dengan penjualan yang sudah selesai (done) tapi belum masuk payroll — diurutkan dari yang paling lama menunggu."
      />

      {me?.canSwitchWorkspace && (
        <div className="mb-4 w-full sm:w-64">
          <SearchableSelect
            value={workspaceId}
            onChange={setWorkspaceId}
            options={workspaces.map((w) => ({ value: w.id, label: w.name }))}
            placeholder="Semua Workspace"
          />
        </div>
      )}

      {isLoading ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Memuat...
        </p>
      ) : items.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Semua penjualan rider sudah masuk payroll.
        </p>
      ) : isMobile ? (
        <div className="space-y-2">
          {items.map((r) => (
            <PendingPaymentCard
              key={r.riderId}
              r={r}
              workspaceLabel={
                showWorkspaceLabel
                  ? workspaceLabelForRider(r.riderId)
                  : undefined
              }
              onShowDetail={setDetail}
            />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Rider</TableHead>
                <TableHead className="text-right">Transaksi Menunggu</TableHead>
                <TableHead>Menunggu Sejak</TableHead>
                <TableHead>Lama Menunggu</TableHead>
                <TableHead className="text-right">Total Omzet</TableHead>
                <TableHead className="text-right">Total Bonus</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((r) => (
                <TableRow key={r.riderId}>
                  <TableCell className="font-medium">
                    {r.riderName}
                    {showWorkspaceLabel && (
                      <span className="mt-0.5 block text-[11px] font-normal text-muted-foreground">
                        {workspaceLabelForRider(r.riderId)}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">{r.pendingCount}</TableCell>
                  <TableCell>{r.oldestDate}</TableCell>
                  <TableCell>
                    <WaitingBadge days={r.daysWaiting} />
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(r.totalRevenue)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(r.totalBonus)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-xs"
                      onClick={() => setDetail(r)}
                    >
                      Lihat Transaksi
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <RiderPendingPaymentDetailDialog
        riderId={detail?.riderId ?? null}
        riderName={detail?.riderName ?? null}
        onOpenChange={(open) => !open && setDetail(null)}
      />
    </div>
  );
}
